import {
  SESSION_PROJECT_VALIDATION_LIMITS,
  SESSION_CLEARANCE_TYPE_OPTIONS,
  SESSION_PROJECT_STATUS_OPTIONS,
} from "../constants/sessionConstants";

// Safe string helper — converts any value to a trimmed string safely
const safeStr = (val) =>
  val != null && val !== undefined ? String(val).trim() : "";

// Validate required field
export const validateRequired = (value, fieldName = "Field") => {
  if (!value || (typeof value === "string" && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

// Validate project name
export const validateSessionProjectName = (projectName) => {
  if (!projectName || !projectName.trim()) {
    return "Project name is required";
  }

  const trimmedName = projectName.trim();

  if (
    trimmedName.length <
    SESSION_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH
  ) {
    return `Project name must be at least ${SESSION_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH} characters`;
  }

  if (
    trimmedName.length >
    SESSION_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH
  ) {
    return `Project name cannot exceed ${SESSION_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH} characters`;
  }

  return null;
};

// Validate source field
export const validateSource = (source) => {
  if (!source || !source.trim()) {
    return null; // Source is optional
  }

  const trimmedSource = source.trim();

  if (
    trimmedSource.length < SESSION_PROJECT_VALIDATION_LIMITS.SOURCE_MIN_LENGTH
  ) {
    return `Source must be at least ${SESSION_PROJECT_VALIDATION_LIMITS.SOURCE_MIN_LENGTH} character`;
  }

  if (
    trimmedSource.length > SESSION_PROJECT_VALIDATION_LIMITS.SOURCE_MAX_LENGTH
  ) {
    return `Source cannot exceed ${SESSION_PROJECT_VALIDATION_LIMITS.SOURCE_MAX_LENGTH} characters`;
  }

  return null;
};

// Validate project status
export const validateProjectStatus = (projectStatus) => {
  if (!projectStatus) {
    return "Project status is required";
  }

  const validStatuses = SESSION_PROJECT_STATUS_OPTIONS.map(
    (option) => option.value,
  );
  if (!validStatuses.includes(projectStatus)) {
    return "Invalid project status";
  }

  return null;
};

// Validate amount
export const validateSessionAmount = (amount, fieldName = "Amount") => {
  if (amount === "" || amount === null || amount === undefined) {
    return null; // Allow empty amounts (will be treated as 0)
  }

  const numAmount = parseFloat(amount);

  if (isNaN(numAmount)) {
    return `${fieldName} must be a valid number`;
  }

  if (numAmount < SESSION_PROJECT_VALIDATION_LIMITS.AMOUNT_MIN) {
    return `${fieldName} cannot be negative`;
  }

  if (numAmount > SESSION_PROJECT_VALIDATION_LIMITS.AMOUNT_MAX) {
    return `${fieldName} cannot exceed ₹99,999 lakhs`;
  }

  return null;
};

// Validate clearance type
export const validateClearanceType = (clearanceType) => {
  if (!clearanceType) {
    return "Clearance type is required";
  }

  const validTypes = SESSION_CLEARANCE_TYPE_OPTIONS.map(
    (option) => option.value,
  );
  if (!validTypes.includes(clearanceType)) {
    return "Invalid clearance type";
  }

  return null;
};

// Validate allocated amount for clearance
export const validateAllocatedAmount = (amount) => {
  if (!amount || amount.toString().trim() === "") {
    return null; // Optional field
  }

  const numAmount = parseFloat(amount);

  if (isNaN(numAmount)) {
    return "Please enter a valid amount";
  }

  if (numAmount < SESSION_PROJECT_VALIDATION_LIMITS.AMOUNT_MIN) {
    return "Amount cannot be negative";
  }

  if (numAmount > SESSION_PROJECT_VALIDATION_LIMITS.AMOUNT_MAX) {
    return "Amount cannot exceed ₹99,999 lakhs";
  }

  return null;
};

// Validate UO Number
export const validateUONumber = (uoNumber) => {
  if (!uoNumber || !uoNumber.trim()) {
    return null; // Optional field
  }

  const trimmed = uoNumber.trim();

  if (trimmed.length < 1) {
    return "UO Number is required";
  }

  if (trimmed.length > 50) {
    return "UO Number cannot exceed 50 characters";
  }

  return null;
};

// Validate clearance date
export const validateClearanceDate = (date) => {
  if (!date) {
    return null; // Optional field
  }

  return null;
};

// Validate complete session project
export const validateSessionProject = (projectData) => {
  const errors = {};

  // Department validation
  const departmentError = validateRequired(
    projectData.department,
    "Department",
  );
  if (departmentError) errors.department = departmentError;

  // Project name validation
  const projectNameError = validateSessionProjectName(projectData.projectName);
  if (projectNameError) errors.projectName = projectNameError;

  // Source validation (optional)
  const sourceError = validateSource(projectData.source);
  if (sourceError) errors.source = sourceError;

  // Project status validation
  const projectStatusError = validateProjectStatus(projectData.projectStatus);
  if (projectStatusError) errors.projectStatus = projectStatusError;

  // Amount validation
  const amountError = validateSessionAmount(projectData.amount, "Amount");
  if (amountError) errors.amount = amountError;

  // Clearance type validation
  const clearanceError = validateClearanceType(projectData.clearanceType);
  if (clearanceError) errors.clearanceType = clearanceError;

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Validate session project clearance data
export const validateSessionClearance = (clearanceData) => {
  const errors = {};

  const amountError = validateAllocatedAmount(clearanceData.allocatedAmount);
  if (amountError) errors.allocatedAmount = amountError;

  const uoError = validateUONumber(clearanceData.uoNumber);
  if (uoError) errors.uoNumber = uoError;

  const dateError = validateClearanceDate(clearanceData.clearanceDate);
  if (dateError) errors.clearanceDate = dateError;

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Sanitize session project data
export const sanitizeSessionProject = (projectData) => {
  const sanitized = { ...projectData };

  // Use safeStr for all string fields to handle null/undefined/non-string safely
  sanitized.projectName = safeStr(sanitized.projectName);
  sanitized.department = safeStr(sanitized.department);
  sanitized.clearanceType = safeStr(sanitized.clearanceType);
  sanitized.source = safeStr(sanitized.source);
  sanitized.projectStatus = safeStr(sanitized.projectStatus);

  // Clean amount - keep in lakhs
  if (
    sanitized.amount === "" ||
    sanitized.amount === null ||
    sanitized.amount === undefined
  ) {
    sanitized.amount = 0;
  } else {
    sanitized.amount = parseFloat(sanitized.amount) || 0;
  }

  // Set default project status if empty
  if (!sanitized.projectStatus || sanitized.projectStatus === "") {
    sanitized.projectStatus = "new_project";
  }

  // Remove undefined values
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] === undefined) {
      delete sanitized[key];
    }
  });

  return sanitized;
};

// Sanitize session clearance data
export const sanitizeSessionClearance = (clearanceData) => {
  const sanitized = { ...clearanceData };

  // Clean string fields
  if (sanitized.uoNumber) {
    sanitized.uoNumber = sanitized.uoNumber.trim();
  }

  // Clean amount - keep in lakhs
  if (
    sanitized.allocatedAmount &&
    sanitized.allocatedAmount.toString().trim() !== ""
  ) {
    sanitized.allocatedAmount = parseFloat(
      sanitized.allocatedAmount.toString().replace(/[^\d.]/g, ""),
    );
  } else {
    sanitized.allocatedAmount = null;
  }

  // Handle date
  if (sanitized.clearanceDate && sanitized.clearanceDate !== "") {
    sanitized.clearanceDate = new Date(sanitized.clearanceDate);
  } else {
    sanitized.clearanceDate = null;
  }

  // Remove empty strings and convert to null
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] === "" || sanitized[key] === undefined) {
      sanitized[key] = null;
    }
  });

  return sanitized;
};

export default {
  validateRequired,
  validateSessionProjectName,
  validateSource,
  validateProjectStatus,
  validateSessionAmount,
  validateClearanceType,
  validateSessionProject,
  sanitizeSessionProject,
  validateAllocatedAmount,
  validateUONumber,
  validateClearanceDate,
  validateSessionClearance,
  sanitizeSessionClearance,
};